import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { convert, formatCompact, formatCurrency, type Currency } from '@fire/core';
import { api } from './api';

/**
 * Display-currency toggle (§9).
 *
 * Everything is *stored* in the user's base currency; this only changes how
 * figures are rendered. The USD/INR rate is fetched from the server rather
 * than hardcoded — a stale constant would silently misstate every number on
 * screen, so when the rate is unavailable the toggle refuses to switch rather
 * than converting at a guess.
 */
interface CurrencyValue {
  display: Currency;
  base: Currency;
  usdInr: number | null;
  rateStale: boolean;
  canSwitch: boolean;
  setDisplay(currency: Currency): void;
  /** Convert a base-currency amount into the display currency. */
  amount(value: number): number;
  format(value: number, fractionDigits?: number): string;
  compact(value: number): string;
}

const CurrencyContext = createContext<CurrencyValue | null>(null);

const STORAGE_KEY = 'fire.displayCurrency';

export function CurrencyProvider({
  base = 'INR',
  children,
}: {
  base?: Currency;
  children: ReactNode;
}) {
  const [display, setDisplayState] = useState<Currency>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === 'USD' || stored === 'INR' ? stored : base;
  });
  const [usdInr, setUsdInr] = useState<number | null>(null);
  const [rateStale, setRateStale] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .fxRate()
      .then(({ usdInr: rate, stale }) => {
        if (cancelled) return;
        setUsdInr(rate);
        setRateStale(stale);
      })
      .catch(() => {
        if (!cancelled) setUsdInr(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setDisplay = useCallback((currency: Currency) => {
    setDisplayState(currency);
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // Private browsing with storage disabled — the toggle still works for
      // this session, it just will not be remembered.
    }
  }, []);

  const value = useMemo<CurrencyValue>(() => {
    const canSwitch = usdInr != null && usdInr > 0;
    const effective: Currency = display !== base && !canSwitch ? base : display;

    const amount = (v: number) =>
      effective === base ? v : convert(v, base, effective, usdInr ?? Number.NaN);

    return {
      display: effective,
      base,
      usdInr,
      rateStale,
      canSwitch,
      setDisplay,
      amount,
      format: (v, fractionDigits = 0) => formatCurrency(amount(v), effective, fractionDigits),
      compact: (v) => formatCompact(amount(v), effective),
    };
  }, [display, base, usdInr, rateStale, setDisplay]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyValue {
  const value = useContext(CurrencyContext);
  if (!value) throw new Error('useCurrency must be used inside a CurrencyProvider');
  return value;
}
